import { useNavigate } from "react-router-dom";
import {
    BookOpen, Bot, ChevronDown, GraduationCap, History, Layers, LogOut,
    MessagesSquare, Presentation as PresentationIcon, ScrollText, Server,
    Settings, Share2, Wrench,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LAYERS } from "../lib/constants";
import {
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const menuItemCls = "cursor-pointer rounded-lg text-xs text-slate-300 focus:bg-current/10 focus:text-current";
const menuLabelCls = "font-mono text-[9px] uppercase tracking-widest text-slate-500";

export default function TopBar({ projects, projectId, onProjectChange, onNewProject, view, onViewChange, layers, onToggleLayer, onOpenModal }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const activeLayers = LAYERS.filter((l) => layers[l.id]).length;

    return (
        <header className="h-14 shrink-0 bg-canvas/80 backdrop-blur-md border-b border-border flex items-center gap-2 xl:gap-3 px-2 sm:px-3 xl:px-4 z-50 overflow-x-auto" data-testid="top-bar">
            <button onClick={() => navigate("/")} data-testid="topbar-home" title="Till överblicken"
                className="flex items-center gap-2 mr-1 hover:opacity-80 transition-opacity">
                <img src="/hamn-emblem.png" alt="HAMN" data-testid="topbar-logo"
                    className="w-8 h-8 rounded-full border border-current/30 object-cover" />
                <span className="font-display font-extrabold tracking-tight text-slate-100 hidden md:block whitespace-nowrap">HAMN ATLAS</span>
            </button>

            <select
                data-testid="project-select"
                value={projectId || ""}
                onChange={(e) => onProjectChange(e.target.value)}
                className="bg-abyss/60 border border-border rounded-lg px-2.5 py-1.5 text-sm text-slate-200 outline-none focus:border-current/60 max-w-[130px] xl:max-w-[190px]"
            >
                {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.demo ? " (demo)" : ""}</option>
                ))}
            </select>
            <button
                data-testid="new-project-button"
                onClick={onNewProject}
                className="hidden sm:block text-xs font-medium text-current hover:text-tide border border-current/30 hover:border-current/60 rounded-lg px-2.5 py-1.5 transition-colors whitespace-nowrap"
            >
                Nytt projekt
            </button>

            <div className="flex items-center bg-abyss/60 border border-border rounded-lg p-0.5 ml-1 xl:ml-2 shrink-0">
                <button
                    data-testid="view-switch-verksamhet"
                    onClick={() => onViewChange("verksamhet")}
                    className={`text-xs font-medium rounded-md px-2 xl:px-3 py-1.5 transition-colors whitespace-nowrap ${view === "verksamhet" ? "bg-current text-abyss" : "text-slate-400 hover:text-slate-200"}`}
                >
                    Verksamhetsbild
                </button>
                <button
                    data-testid="view-switch-flode"
                    onClick={() => onViewChange("flode")}
                    className={`text-xs font-medium rounded-md px-2 xl:px-3 py-1.5 transition-colors whitespace-nowrap ${view === "flode" ? "bg-current text-abyss" : "text-slate-400 hover:text-slate-200"}`}
                >
                    Flödesbild
                </button>
                <button
                    data-testid="view-switch-roller"
                    onClick={() => onViewChange("roller")}
                    className={`text-xs font-medium rounded-md px-2 xl:px-3 py-1.5 transition-colors whitespace-nowrap ${view === "roller" ? "bg-current text-abyss" : "text-slate-400 hover:text-slate-200"}`}
                >
                    Rollmatris
                </button>
            </div>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button data-testid="perspective-menu-button"
                        className="flex items-center gap-1.5 rounded-lg border border-border bg-abyss/60 px-2 xl:px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:border-current/50 hover:text-current transition-colors shrink-0">
                        <Layers className="w-4 h-4" />
                        <span className="hidden lg:inline">Perspektiv</span>
                        <span className="font-mono text-[9px] text-slate-500">{activeLayers}/{LAYERS.length}</span>
                        <ChevronDown className="w-3 h-3 text-slate-500" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" sideOffset={8} className="min-w-[210px] rounded-xl bg-canvas border-border text-slate-200 shadow-2xl" data-testid="perspective-menu">
                    <DropdownMenuLabel className={menuLabelCls}>Perspektivlager</DropdownMenuLabel>
                    {LAYERS.map((l) => (
                        <DropdownMenuCheckboxItem
                            key={l.id}
                            data-testid={`layer-toggle-${l.id}`}
                            checked={layers[l.id]}
                            onCheckedChange={() => onToggleLayer(l.id)}
                            onSelect={(e) => e.preventDefault()}
                            className="cursor-pointer rounded-lg text-xs text-slate-300 focus:bg-current/10 focus:text-current"
                        >
                            <span className="w-2 h-2 rounded-full mr-1" style={{ background: layers[l.id] ? l.color : "#334155" }} />
                            {l.label}
                        </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex-1" />

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button data-testid="tools-menu-button"
                        className="flex items-center gap-1.5 rounded-lg border border-border bg-abyss/60 px-2 xl:px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:border-current/50 hover:text-current transition-colors shrink-0">
                        <Wrench className="w-4 h-4" />
                        <span className="hidden lg:inline">Verktyg</span>
                        <ChevronDown className="w-3 h-3 text-slate-500" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="min-w-[235px] rounded-xl bg-canvas border-border text-slate-200 shadow-2xl" data-testid="tools-menu">
                    <DropdownMenuLabel className={menuLabelCls}>Arbetsytor</DropdownMenuLabel>
                    <DropdownMenuItem data-testid="open-hub-button" onClick={() => navigate("/hub")} className={menuItemCls}>
                        <MessagesSquare className="w-4 h-4" /> Consultant Hub
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="open-utbildning-button" onClick={() => navigate("/utbildning")} className={menuItemCls}>
                        <GraduationCap className="w-4 h-4" /> Utbildning & föreläsning
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="open-rollspegling-button" disabled={!projectId}
                        onClick={() => projectId && navigate(`/rollspegling/${projectId}`)} className={menuItemCls}>
                        <Bot className="w-4 h-4" /> Rollspegling
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuLabel className={menuLabelCls}>Projektdata</DropdownMenuLabel>
                    <DropdownMenuItem data-testid="open-sources-button" onClick={() => onOpenModal("sources")} className={menuItemCls}>
                        <BookOpen className="w-4 h-4" /> Empiri & källor
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="open-links-button" onClick={() => onOpenModal("links")} className={menuItemCls}>
                        <Share2 className="w-4 h-4" /> Medarbetarlänkar
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="open-system-button" onClick={() => onOpenModal("system")} className={menuItemCls}>
                        <Server className="w-4 h-4" /> Systemöversikt
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuLabel className={menuLabelCls}>Spårbarhet</DropdownMenuLabel>
                    <DropdownMenuItem data-testid="open-versions-button" onClick={() => onOpenModal("versions")} className={menuItemCls}>
                        <History className="w-4 h-4" /> Versionshistorik
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="open-audit-button" onClick={() => onOpenModal("audit")} className={menuItemCls}>
                        <ScrollText className="w-4 h-4" /> Audit-logg
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {projectId && (
                <button
                    data-testid="presentation-mode-button"
                    onClick={() => navigate(`/view/${projectId}`)}
                    className="flex items-center gap-2 text-xs font-semibold bg-buoy/15 text-buoy border border-buoy/40 rounded-lg px-2.5 xl:px-3 py-1.5 hover:bg-buoy/25 transition-colors whitespace-nowrap shrink-0"
                >
                    <PresentationIcon className="w-4 h-4" />
                    <span className="hidden lg:inline">Executive Dashboard</span>
                </button>
            )}
            <div className="flex items-center gap-2 ml-1 xl:ml-2 pl-2 xl:pl-3 border-l border-border shrink-0">
                <span className="font-mono text-[10px] text-slate-500 hidden 2xl:block">{user?.email}</span>
                <button data-testid="open-settings-button" onClick={() => onOpenModal("settings")} title="Inställningar"
                    className="p-2 rounded-lg text-slate-400 hover:text-current hover:bg-current/10 transition-colors">
                    <Settings className="w-4 h-4" />
                </button>
                <button data-testid="logout-button" onClick={logout} title="Logga ut"
                    className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                    <LogOut className="w-4 h-4" />
                </button>
            </div>
        </header>
    );
}
